import * as yup from 'yup';

/**
 * Wellness (E13 / E14 / E15) — the per-event-type payload codecs of the offline batch transport
 * (plan 03 §2.4, 00 §8-Q9). Authored ONCE here, run on BOTH ends: the device validates at
 * ENQUEUE time — a malformed proposal fails at the screen rather than sitting in the queue for
 * hours waiting for the server to bounce it — and the server runs the same schema as pipeline
 * step (1).
 *
 * ADDITIVE ONLY, one entry per built slice, in board order. A type is never removed or renamed: a
 * device that has been offline may still be holding a proposal of any type this list has carried,
 * and the enum is what decides whether the server will take it.
 *
 * `subject` IS ABSENT FROM EVERY SCHEMA, and that is the security property rather than an
 * oversight: the board marks it `generated: true` (server-injected), and the resolver stamps the
 * session's own subject onto every proposal before validation. A client that could name a subject
 * could write into somebody else's log.
 */

export const WELLNESS_EVENT_TYPES = [
  'MeditationSessionStarted', // E13 · PlayMeditation            (buildOrder 3)
  'MeditationSessionFinished', // E13 · FinishMeditationSession   (buildOrder 6)
  'RelaxationVideoStarted', // E14 · PlayRelaxationVideo          (buildOrder 4)
  'RelaxationVideoFinished', // E14 · FinishRelaxationVideo       (buildOrder 6)
  'ArticleViewStarted', // E15 · OpenLearnArticle                 (buildOrder 3)
  'ArticleViewFinished', // E15 · FinishLearnArticle              (buildOrder 5)
  'LearnVideoStarted', // E15 · PlayLearnVideo                    (buildOrder 9)
  'LearnVideoFinished', // E15 · FinishLearnVideo                 (buildOrder 12)
] as const;
export type WellnessEventTypeName = (typeof WELLNESS_EVENT_TYPES)[number];

/**
 * Seconds. Non-negative and finite; the UPPER bound is the decider's business (`position` may not
 * exceed `duration` by more than the decoder-vs-catalogue tolerance), because that is a rule about
 * a pair rather than about a field.
 */
const seconds = yup.number().min(0).required();

/**
 * `secondsPlayed` — how much of the material the player really advanced through (founder ruling
 * 2026-09-13 "W1. a", E13/E14/E15 `D-w1-seconds-played-2026-09-14`).
 *
 * OPTIONAL IN BOTH DIRECTIONS, and that is the entire migration: a client that predates the ruling
 * omits it and its proposals stay valid for ever (a phone can be offline for a week and its queue
 * must still drain), while a projection reads `secondsPlayed ?? null`. No `schemaVersion` moved,
 * no upcaster is owed, and `submitWellnessEvents` takes it with no new operation.
 *
 * Non-negative and finite is all this end asserts. The UPPER bound — never more than `duration`
 * plus the overrun tolerance — is applied on the DEVICE, by the rule that produces the number
 * (`@cc/domain` `wellness/playedSeconds.ts`), and deliberately not refused here: a refusal is
 * permanent for that device, the proposal is retried on every flush, and the queue would jam
 * behind it over a number nothing folds.
 */
const secondsPlayed = yup.number().min(0).optional();

/** 0–100. A "depth" of 480 is pixels leaking through an interface, and is refused on both ends. */
const percentage = yup.number().min(0).max(100).required();

const mediaBase = { videoId: yup.string().trim().required() };

export const meditationSessionStartedPayloadSchema = yup.object({
  url: yup.string().trim().required(),
  title: yup.string().defined(),
});

export const meditationSessionFinishedPayloadSchema = yup.object({
  url: yup.string().trim().required(),
  title: yup.string().defined(),
  position: seconds,
  duration: seconds,
  secondsPlayed,
});

export const relaxationVideoStartedPayloadSchema = yup.object({ ...mediaBase });

export const relaxationVideoFinishedPayloadSchema = yup.object({
  ...mediaBase,
  position: seconds,
  duration: seconds,
  secondsPlayed,
});

export const articleViewStartedPayloadSchema = yup.object({
  articleId: yup.string().trim().required(),
  // `en` | `ru` — the localisation actually read; the join the shipped telemetry could not make
  language: yup.mixed<'en' | 'ru'>().oneOf(['en', 'ru']).required(),
  title: yup.string().defined(),
});

export const articleViewFinishedPayloadSchema = yup.object({
  articleId: yup.string().trim().required(),
  scrollDepth: percentage,
  // no upper bound: an article left open on a nightstand really was left open
  duration: seconds,
});

export const learnVideoStartedPayloadSchema = yup.object({ ...mediaBase });

export const learnVideoFinishedPayloadSchema = yup.object({
  ...mediaBase,
  position: seconds,
  duration: seconds,
  secondsPlayed,
  // the ARTICLE finish is deliberately not given one: `scrollDepth` already answers "how much was
  // reached", and nothing about reading an article is a played interval (W1 (a)'s own reasoning)
});

/** The type → payload-codec table both ends read. */
export const wellnessPayloadSchemas: Record<
  WellnessEventTypeName,
  yup.AnyObjectSchema
> = {
  MeditationSessionStarted: meditationSessionStartedPayloadSchema,
  MeditationSessionFinished: meditationSessionFinishedPayloadSchema,
  RelaxationVideoStarted: relaxationVideoStartedPayloadSchema,
  RelaxationVideoFinished: relaxationVideoFinishedPayloadSchema,
  ArticleViewStarted: articleViewStartedPayloadSchema,
  ArticleViewFinished: articleViewFinishedPayloadSchema,
  LearnVideoStarted: learnVideoStartedPayloadSchema,
  LearnVideoFinished: learnVideoFinishedPayloadSchema,
};

/** One wire proposal, validated shape and all. */
export const wellnessEventInputSchema = yup.object({
  eventId: yup.string().trim().required(),
  type: yup
    .mixed<WellnessEventTypeName>()
    .oneOf([...WELLNESS_EVENT_TYPES])
    .required(),
  occurredAt: yup.date().required(),
  clientSeq: yup.number().integer().min(0).required(),
  payload: yup
    .mixed<Record<string, unknown>>()
    .required()
    .test(
      'wellness-payload',
      'validators.required',
      function validatePayload(payload) {
        const type = (this.parent as { type?: WellnessEventTypeName })?.type;
        const schema = type ? wellnessPayloadSchemas[type] : undefined;
        if (!schema) return true; // the `type` oneOf above already reported it
        try {
          schema.validateSync(payload, { abortEarly: false, strict: false });
          return true;
        } catch (e) {
          return this.createError({
            message: (e as yup.ValidationError).errors?.join(', '),
          });
        }
      },
    ),
});

/**
 * How many proposals ONE flush may carry.
 *
 * The client sends `CLIENT_OUTBOX_BATCH` (50) and its whole queue is capped at 500, so nothing the
 * app does comes near this. A CLIENT IS NOT A TRUST BOUNDARY, though: without a cap, one request
 * could ask the server to decide and append an unbounded array, and the append is a single
 * transaction. Four times the client's batch leaves every legitimate flush — including a queue
 * drained in one go by some future client — far inside the limit.
 */
export const WELLNESS_EVENTS_PER_BATCH_MAX = 200;

/** The whole mutation's arguments — what the resolver validates in one call. */
export const submitWellnessEventsArgsSchema = yup.object({
  events: yup
    .array()
    .of(wellnessEventInputSchema.required())
    .min(1)
    .max(WELLNESS_EVENTS_PER_BATCH_MAX)
    .required(),
});

export type WellnessEventInputValidated = yup.Asserts<
  typeof wellnessEventInputSchema
>;

export const wellnessValidators = {
  WELLNESS_EVENTS_PER_BATCH_MAX,
  submitWellnessEventsArgsSchema,
  wellnessEventInputSchema,
  wellnessPayloadSchemas,
};
